// Thin wrapper around the channels service's policy/draft-authoring
// endpoints (see services/channels/src/channels_service/routers/
// policies.py and drafts.py).
//
// Same as api.js: the frontend and the reverse proxy always share one
// origin (see services/proxy), so a relative path is all we need here -
// no base URL or CORS configuration required. The proxy forwards
// /api/policies and /api/drafts to the channels service unmodified.
const API_BASE = '/api';

export class DraftsError extends Error {
  constructor(message, { code, status } = {}) {
    super(message);
    this.name = 'DraftsError';
    this.code = code;
    this.status = status;
  }
}

/**
 * Shared fetch + error-shape handling for every call in this module -
 * same JSON-body/error_description convention as api.js's registerUser,
 * just factored out since this module talks to more than one endpoint.
 */
async function request(accessToken, path, { method = 'GET', body } = {}) {
  let response;
  try {
    response = await fetch(`${API_BASE}${path}`, {
      method,
      headers: {
        ...(body !== undefined ? { 'Content-Type': 'application/json' } : {}),
        Authorization: `Bearer ${accessToken}`,
      },
      ...(body !== undefined ? { body: JSON.stringify(body) } : {}),
    });
  } catch (err) {
    throw new DraftsError(
      'Could not reach the server. Check your connection and try again.',
      { code: 'network_error' },
    );
  }

  let parsed = null;
  try {
    parsed = await response.json();
  } catch (err) {
    // Non-JSON response body (e.g. a 204); fall through with parsed === null.
  }

  if (!response.ok) {
    const message = parsed?.error_description ?? 'Something went wrong. Please try again.';
    throw new DraftsError(message, { code: parsed?.error, status: response.status });
  }

  return parsed;
}

/**
 * Calls GET /api/drafts.
 *
 * Resolves with the caller's own drafts (`DraftOut[]`, newest first,
 * possibly empty) on success. Rejects with a DraftsError on any known
 * failure (e.g. 401) or unexpected/network error, with a human-readable
 * `message`.
 */
export async function listDrafts(accessToken) {
  const body = await request(accessToken, '/drafts');
  return Array.isArray(body) ? body : [];
}

/**
 * Calls GET /api/policies?owner={ownerUsername} - the caller's own
 * policies. A draft against an existing policy can only ever be opened
 * by that policy's owner (see require_owner in routers/drafts.py), so
 * there's no point offering anyone else's policies to the "edit
 * existing policy" flow even though the endpoint itself would happily
 * return public ones too.
 *
 * Resolves with the first page of policies (`PolicyOut[]`, possibly
 * empty - capped at 100, plenty for a picker). Rejects with a
 * DraftsError otherwise.
 */
export async function listOwnPolicies(accessToken, ownerUsername) {
  const params = new URLSearchParams({ owner: ownerUsername, limit: '100' });
  const body = await request(accessToken, `/policies?${params}`);
  return Array.isArray(body?.items) ? body.items : [];
}

/**
 * The caller's own policies that are eligible for the "edit existing
 * policy" freeform flow: only ones whose *current* revision was itself
 * authored from freeform text (`RevisionOut.freeform_text` in
 * schemas_policy.py). The UI only ever creates/edits *freeform* drafts -
 * there's no rules builder here - so a policy whose current rules were
 * hand-built (freeform_text null) isn't offered: opening a freeform
 * draft against it and promoting would silently replace that hand-built
 * rule set with whatever the AI compiles from a from-scratch description.
 *
 * Fetches the caller's policies, then (in parallel) each one's latest
 * revision to read `freeform_text` - technically N+1, but N is a
 * picker's worth of policies, not a paginated list, and there's no
 * "policies whose latest revision has freeform_text" index to query
 * directly instead.
 *
 * Resolves with `{ owner, name, freeformText }[]` - `freeformText` is
 * the current revision's text, used to pre-populate the requirements
 * box when this policy is selected. Rejects with a DraftsError only if
 * the initial policy list itself fails to load; a single policy's
 * revision lookup failing just drops that policy from the result
 * instead of failing the whole call.
 */
export async function listOwnFreeformPolicies(accessToken, ownerUsername) {
  const policies = await listOwnPolicies(accessToken, ownerUsername);
  const entries = await Promise.all(
    policies.map(async (policy) => {
      try {
        const revisions = await request(
          accessToken,
          `/policies/${policy.owner_username}/${policy.name}/revisions?limit=1`,
        );
        const latest = Array.isArray(revisions) ? revisions[0] : null;
        if (!latest?.freeform_text) return null;
        return {
          owner: policy.owner_username,
          name: policy.name,
          freeformText: latest.freeform_text,
        };
      } catch (err) {
        return null;
      }
    }),
  );
  return entries.filter((entry) => entry !== null);
}

/**
 * Calls POST /api/drafts.
 *
 * `{ name, freeformText }` starts a draft for a brand-new policy;
 * `{ policy, freeformText }` (policy = "{owner}/{name}" of an existing
 * one the caller owns) opens a draft against it instead. Either way
 * this only ever creates a *freeform* draft - the UI doesn't offer a
 * rules builder, so `default_effect` is fixed to the conservative
 * 'deny' (safe-by-default until someone reviews/tightens it) and
 * `authored_by` is 'ai', since the eventual rules come from compiling
 * `freeform_text`, not from manual rule entry.
 *
 * Resolves with the created `DraftDetail` (HTTP 201) on success.
 * Rejects with a DraftsError otherwise - notably 409 (`draft_exists`)
 * when `policy` already has an active draft.
 */
export async function createDraft(accessToken, { policy, name, freeformText }) {
  return request(accessToken, '/drafts', {
    method: 'POST',
    body: {
      policy: policy ?? null,
      name: name ?? null,
      default_effect: 'deny',
      authored_by: 'ai',
      freeform_text: freeformText,
    },
  });
}

/**
 * Calls GET /api/drafts/{draftId}.
 *
 * Resolves with the draft (`DraftDetail`) on success. Rejects with a
 * DraftsError otherwise - notably 404 (`not_found`) if the draft
 * doesn't exist or isn't owned by the caller (drafts.py's
 * `_get_draft_or_404` never distinguishes the two).
 */
export async function getDraft(accessToken, draftId) {
  return request(accessToken, `/drafts/${draftId}`);
}

/**
 * Calls PATCH /api/drafts/{draftId} with just `freeform_text` - the
 * only field this UI ever lets someone revise on an already-created
 * draft. Note that setting it (per drafts.py's `patch_draft`) always
 * clears the draft's `compiled_at`, so a freshly edited draft goes back
 * to "not compiled" until the (future) `draft.compile_freeform` job
 * runs again - see the `draft_not_compiled` caveat on `promoteDraft`.
 *
 * Resolves with the updated `DraftDetail` on success. Rejects with a
 * DraftsError otherwise (404 `not_found`, 422 on an over-length body).
 */
export async function updateDraftFreeformText(accessToken, draftId, freeformText) {
  return request(accessToken, `/drafts/${draftId}`, {
    method: 'PATCH',
    body: { freeform_text: freeformText },
  });
}

/**
 * Calls DELETE /api/drafts/{draftId}. Permanently discards the draft
 * (and any compiled rules sitting on it) - there is no undo, and
 * unlike promoting, nothing is written to `policy`/`policy_revision`.
 *
 * Resolves with `undefined` (HTTP 204, no body) on success. Rejects
 * with a DraftsError otherwise (404 `not_found`).
 */
export async function deleteDraft(accessToken, draftId) {
  return request(accessToken, `/drafts/${draftId}`, { method: 'DELETE' });
}

/**
 * Calls POST /api/drafts/{draftId}/promote - the only write path into
 * `policy`/`policy_revision`/`policy_rule` (see drafts.py's
 * `promote_draft`). On success the draft is gone (promoted, not just
 * archived); a fresh one would need to be created to keep editing.
 *
 * Every draft this UI creates carries `freeform_text`, and `patch_draft`
 * always clears `compiled_at` whenever `freeform_text` is set - so
 * until the `draft.compile_freeform` job exists to (re-)compile it into
 * rules, this will reliably 400 with `draft_not_compiled` for any draft
 * that was ever edited here. That's a real, documented backend
 * response, not a bug in this call - surface it like any other error.
 *
 * Resolves with the `PromoteOut` (HTTP 201: `{ policy, policy_id,
 * revision }`) on success. Rejects with a DraftsError otherwise -
 * notably 400 (`draft_not_compiled`, `revision_drift`) or 409
 * (`policy_exists`).
 */
export async function promoteDraft(accessToken, draftId) {
  return request(accessToken, `/drafts/${draftId}/promote`, { method: 'POST' });
}
